import { Route } from '@angular/router';
import {
  buildPortalDashboardChildRoutes,
  buildPortalPermissionGuard,
  getPortalDefaultChildPath,
} from '@dlap-ui/shared-forms/shell/portal-shell/portal-shell';
import { authGuard } from '@dlap-ui/core';
import type { PortalAccessConfig } from '@dlap-ui/shared-forms/auth/otp-form/otp-form';
import { environment } from '../environments/environment';
import { DEEDS_PORTAL_CONFIG } from './pages/dashboard-layout/portal.config';

const dashboardChildRoutes: Route[] = buildPortalDashboardChildRoutes(DEEDS_PORTAL_CONFIG);

const DEEDS_PORTAL_ACCESS: PortalAccessConfig = {
  allowedCategories: ['Deeds'],
  allowMissingCategory: true,
  categoryRedirects: {
    bank: environment.portalUrls.bank,
    'bond holding': environment.portalUrls.bank,
    'law firm': environment.portalUrls.lawFirm,
    'high court': environment.portalUrls.highCourt,
  },
  unauthorizedMessage: 'Not allowed to use this portal.',
};

export const appRoutes: Route[] = [
  {
    path: '',
    loadComponent: () => import('./pages/landing/landing').then((m) => m.Landing),
    pathMatch: 'full',
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./pages/legal/privacy-policy/privacy-policy').then((m) => m.PrivacyPolicy),
  },
  {
    path: 'terms',
    loadComponent: () => import('./pages/legal/terms/terms').then((m) => m.Terms),
  },
  {
    path: 'accessibility',
    loadComponent: () => import('./pages/legal/accessibility/accessibility').then((m) => m.Accessibility),
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./pages/auth-layout/auth-layout').then((m) => m.AuthLayout),
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/login/login').then((m) => m.Login),
      },
      {
        path: 'otp',
        data: {
          production: environment.production,
          portalAccess: DEEDS_PORTAL_ACCESS,
        },
        loadComponent: () =>
          import('@dlap-ui/shared-forms/auth/otp-form/otp-form').then((m) => m.OtpForm),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('@dlap-ui/shared-forms/auth/forgot-password-form/forgot-password-form').then((m) => m.ForgotPasswordForm),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('@dlap-ui/shared-forms/auth/reset-password-form/reset-password-form').then((m) => m.ResetPasswordForm),
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: 'overview',
    loadComponent: () =>
      import('./pages/dashboard-layout/dashboard-layout').then(
        (m) => m.DashboardLayout,
      ),
    canActivate: [authGuard],
    children: [
      ...dashboardChildRoutes,
      {
        path: 'apply',
        loadComponent: () =>
          import('./pages/apply/application-workflow/application-workflow').then(
            (m) => m.ApplicationWorkflow,
          ),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'applications'),
      },
      {

        path: 'plots/new',
        loadComponent: () =>
          import('./pages/properties/new-property/new-property').then((m) => m.NewProperty),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'plots'),
      },
      {
        path: 'plots/view/:id',
        loadComponent: () =>
          import('./pages/properties/plot-detail/plot-detail').then((m) => m.PlotDetail),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'plots'),
      },
      {

        path: 'farms/new',
        loadComponent: () =>
          import('./pages/farms/new-farm/new-farm').then((m) => m.NewFarm),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'farms'),
      },
      {
        path: 'farms/view/:id',
        loadComponent: () =>
          import('./pages/farms/farm-detail/farm-detail').then((m) => m.FarmDetail),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'farms'),
      },
      {
        path: 'plot-staging/new',
        loadComponent: () =>
          import('./pages/plot-staging/new-plot-staging/new-plot-staging').then((m) => m.NewPlotStaging),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'plot-staging'),
      },
      {
        path: 'plot-staging/edit/:id',
        loadComponent: () =>
          import('./pages/plot-staging/new-plot-staging/new-plot-staging').then((m) => m.NewPlotStaging),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'plot-staging'),
      },
      {
        path: 'farm-staging/new',
        loadComponent: () =>
          import('./pages/farm-staging/new-farm-staging/new-farm-staging').then((m) => m.NewFarmStaging),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'farm-staging'),
      },
      {
        path: 'farm-staging/edit/:id',
        loadComponent: () =>
          import('./pages/farm-staging/new-farm-staging/new-farm-staging').then((m) => m.NewFarmStaging),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'farm-staging'),
      },
      {
        path: 'provinces/:provinceId/districts',
        loadComponent: () =>
          import('./pages/system-parameters/provinces/districts/districts').then((m) => m.Districts),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'provinces'),
      },
      {
        path: 'provinces/:provinceId/districts/:districtId/townships',
        loadComponent: () =>
          import('./pages/system-parameters/provinces/districts/townships/townships').then((m) => m.Townships),
        canActivate: buildPortalPermissionGuard(DEEDS_PORTAL_CONFIG, 'provinces'),
      },
      { path: '', redirectTo: getPortalDefaultChildPath(DEEDS_PORTAL_CONFIG), pathMatch: 'full' },
    ],
  },
];
